import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { get, round, noop } from 'lodash-es';
import classNames from 'classnames';
import Button from 'reactstrap/lib/Button';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import { srpLinkBuilder } from 'site-modules/shared/utils/srp-link-constructor';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { numberWithCommas, objectToQueryString } from 'site-modules/shared/utils/string';
import { INVENTORY_TYPES, INVENTORY_TYPES_LOWERCASE } from 'client/constants/inventory-types';
import { InventoryEntities } from 'client/data/models/inventory';
import { getFreshHistoryParam } from 'site-modules/shared/components/search-by-module/search-by-module-utils';
import { Link } from 'site-modules/shared/components/link/link';
import { venomHistory } from 'client/utils/history/venom-history';
import { useTimeout } from 'site-modules/shared/hooks/use-timeout';

import './zip-long-distance-confirm-screen.scss';

export function ZipLongDistanceConfirmScreen({
  submitButtonColor = 'blue-50',
  inventory,
  zipCode = PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  make = '',
  model = '',
  onShowLeadForm = null,
  classes = '',
  creativeId = '',
  toggleDrawer = noop,
}) {
  const [setRouteChangeTimeout] = useTimeout();

  useEffect(() => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_WIDGET_VIEW,
      event_data: {
        action_name: TrackingConstant.ACTION_WIDGET_VIEW,
        action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
        creative_id: `${creativeId}-ZIP_CONFIRM`,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const params = {
    make: makeNiceName(make),
    model: [makeNiceName(make), makeNiceName(model)].join('|'),
    inventorytype:
      get(inventory, 'type') === INVENTORY_TYPES.NEW
        ? INVENTORY_TYPES_LOWERCASE.NEW
        : [INVENTORY_TYPES_LOWERCASE.USED, INVENTORY_TYPES_LOWERCASE.CPO],
  };

  const handleSearchClick = useCallback(
    e => {
      e.preventDefault();
      toggleDrawer();
      const href = e.currentTarget.getAttribute('href');

      requestAnimationFrame(() => {
        setRouteChangeTimeout(() => {
          venomHistory.push(href);
        }, 0);
      });
    },
    [toggleDrawer, setRouteChangeTimeout]
  );

  return (
    <div
      className={classNames('zip-long-distance-confirm-screen', classes)}
      data-tracking-parent={`${creativeId}-ZIP_CONFIRM`}
      data-no-widget-view
    >
      <h2 className="heading-4 mb-1">
        This car is {numberWithCommas(round(get(inventory, 'dealerInfo.distance', 0)))} miles from {zipCode}
      </h2>
      <div className="mb-1">Search cars near you or confirm you want to check availability.</div>
      <div className="mb-0_5">
        <Button
          to={`${srpLinkBuilder(params)}&${objectToQueryString(getFreshHistoryParam())}`}
          onClick={handleSearchClick}
          tag={Link}
          color={submitButtonColor}
          className="w-100 btn-lg px-1 py-0_5 fw-medium text-transform-none"
          data-tracking-id="search"
          data-tracking-ignore-value
        >
          <span>Search cars near {zipCode}</span>
        </Button>
      </div>
      <div className="pb-1">
        <Button
          onClick={onShowLeadForm}
          color="link"
          className="w-100 btn-lg px-1 py-0_5 fw-medium text-transform-none text-primary-darker text-decoration-none"
          data-tracking-id="check_availability"
          data-tracking-ignore-value
        >
          <span>Check Availability</span>
          <i className="icon-arrow-right4 xsmall ms-0_25" aria-hidden />
        </Button>
      </div>
    </div>
  );
}

ZipLongDistanceConfirmScreen.propTypes = {
  inventory: InventoryEntities.InventoryVin.isRequired,
  zipCode: PropTypes.string,
  submitButtonColor: PropTypes.string,
  make: PropTypes.string,
  model: PropTypes.string,
  onShowLeadForm: PropTypes.func,
  classes: PropTypes.string,
  creativeId: PropTypes.string,
  toggleDrawer: PropTypes.func,
};
